import { styled } from '@mui/material'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React from 'react'

import { TWhatsappFlowElement } from '@/app/types'

type Props = {
  element: TWhatsappFlowElement
}

const getQuestionLabel = (question: string, required = false): string => {
  if (!required) {
    return question
  }

  return question ? `${question} *` : '*'
}

const WhatsappFlowElement: React.FC<Props> = ({ element }) => {
  switch (true) {
    case !!element.heading:
      return (
        <WrapText variant="subtitle1" fontWeight={700}>
          {element.heading?.text}
        </WrapText>
      )
    case !!element.subheading:
      return (
        <WrapText color="textSecondary" variant="body2">
          {element.subheading?.text}
        </WrapText>
      )
    case !!element.singleSelect:
      return (
        <Stack spacing={1}>
          <WrapText variant="body2" fontWeight={600}>
            {getQuestionLabel(element.singleSelect?.question || '', element.singleSelect?.required)}
          </WrapText>
          {element.singleSelect?.helpText && (
            <WrapText color="textSecondary" variant="caption">
              {element.singleSelect.helpText}
            </WrapText>
          )}
          <Stack spacing={0.5}>
            {element.singleSelect?.options.map((option) => (
              <FormControlLabel key={option.id} control={<Radio size="small" disabled />} label={option.title} />
            ))}
          </Stack>
        </Stack>
      )
    case !!element.multiSelect:
      return (
        <Stack spacing={1}>
          <WrapText variant="body2" fontWeight={600}>
            {getQuestionLabel(element.multiSelect?.question || '', element.multiSelect?.required)}
          </WrapText>
          {element.multiSelect?.helpText && (
            <WrapText color="textSecondary" variant="caption">
              {element.multiSelect.helpText}
            </WrapText>
          )}
          <Stack spacing={0.5}>
            {element.multiSelect?.options.map((option) => (
              <FormControlLabel key={option.id} control={<Checkbox size="small" disabled />} label={option.title} />
            ))}
          </Stack>
        </Stack>
      )
    case !!element.list:
      return (
        <Stack spacing={1}>
          <WrapText variant="body2" fontWeight={600}>
            {element.list?.question}
          </WrapText>
          {element.list?.helpText && (
            <WrapText color="textSecondary" variant="caption">
              {element.list.helpText}
            </WrapText>
          )}
          <Stack spacing={0.5}>
            {element.list?.options.map((option) => (
              <FormControlLabel key={option.id} control={<Radio size="small" disabled />} label={option.title} />
            ))}
          </Stack>
        </Stack>
      )
    case !!element.shortText:
      return (
        <Stack spacing={1}>
          <WrapText variant="body2" fontWeight={600}>
            {getQuestionLabel(element.shortText?.question || '', element.shortText?.required)}
          </WrapText>
          {element.shortText?.helpText && (
            <WrapText color="textSecondary" variant="caption">
              {element.shortText.helpText}
            </WrapText>
          )}
          <TextField disabled placeholder="Your answer" size="small" />
        </Stack>
      )
    case !!element.longText:
      return (
        <Stack spacing={1}>
          <WrapText variant="body2" fontWeight={600}>
            {getQuestionLabel(element.longText?.question || '', element.longText?.required)}
          </WrapText>
          {element.longText?.helpText && (
            <WrapText color="textSecondary" variant="caption">
              {element.longText.helpText}
            </WrapText>
          )}
          <TextField disabled placeholder="Your answer" size="small" multiline minRows={4} />
        </Stack>
      )
    case !!element.datePicker:
      return (
        <Stack spacing={1}>
          <WrapText variant="body2" fontWeight={600}>
            {element.datePicker?.question}
          </WrapText>
          <TextField disabled placeholder="Select date" size="small" />
        </Stack>
      )
    case !!element.body:
      return <WrapText variant="body2">{element.body?.text}</WrapText>
    case !!element.caption:
      return (
        <WrapText color="textSecondary" variant="caption">
          {element.caption?.text}
        </WrapText>
      )
    case !!element.richText:
      return (
        <Stack spacing={0.5}>
          {element.richText?.texts.map((text, index) => (
            <WrapText key={index} variant="body2">
              {text}
            </WrapText>
          ))}
        </Stack>
      )
    case !!element.media:
      return (
        <WrapText color="textSecondary" variant="caption">
          {element.media?.file}
        </WrapText>
      )
    case !!element.footer:
      return (
        <Stack alignItems="center">
          <Button disabled size="small" variant="contained">
            {element.footer?.label}
          </Button>
        </Stack>
      )
    default:
      return null
  }
}

const WrapText = styled(Typography)({
  wordWrap: 'break-word',
})

export default WhatsappFlowElement

export type { Props as WhatsappFlowElementProps }
